import { useMemo, useState } from 'react';
import type { CSSProperties, FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Avatar, Badge, Col, Dropdown, Row, Skeleton } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { marketHoursQueryOptions } from '@/queries';
import type { FmpExchangeMarketHoliday, FmpExchangeMarketHour } from '@/queries';
import { CompactHoverCardSection, CompactHoverCardSurface } from '@/shared/components/cards';
import { EmptyView } from '@/shared/components/layouts/views/EmptySection';
import SmallTag from '@/shared/components/tags/SmallTag';
import MiddleDot from '@/shared/components/typography/MiddleDot';
import { formatDate, formatTimezoneLabel, formatTimezoneRegion } from '@/utilities';
import { TbArrowRight, TbBuildingBank } from '@/vendor/react-icons-tb';

import { getFinancialMarketHubHours, getPrimaryFinancialMarketHour } from '../marketHubs';
import { getMarketClosureReason, getNextMarketSessionTransition } from '../marketSession';

interface MarketStatusPopoverProps {
    city: string;
    flagSrc?: string;
    isError?: boolean;
    isLoading?: boolean;
    marketHours: readonly FmpExchangeMarketHour[];
    now: Date;
    popoverWidth?: CSSProperties['width'];
    timeZone: string;
}

const stripUtcOffset = (value: string) => value.replace(/\s[+-]\d{2}:\d{2}$/u, '');

const ExchangeInformationSkeleton: FC<{ rowCount?: number }> = ({ rowCount = 2 }) => (
    <div aria-label='Loading exchange information' role='status'>
        {Array.from({ length: rowCount }, (_, index) => (
            <Row key={index} align='middle' style={{ marginBottom: index === rowCount - 1 ? 0 : '0.5rem' }}>
                <Col flex='auto'>
                    <Skeleton.Input active size='small' style={{ height: '1rem', width: '8rem' }} />
                </Col>
                <Col flex='none'>
                    <Skeleton.Input active size='small' style={{ height: '1rem', width: '6rem' }} />
                </Col>
            </Row>
        ))}
    </div>
);

const SessionTimeRange: FC<{ closureReason?: string; marketHour: FmpExchangeMarketHour }> = ({
    closureReason,
    marketHour,
}) => {
    const { token } = useAntDesignTheme();
    const { closingAdditional, closingHour, openingAdditional, openingHour } = marketHour;

    if (closureReason) {
        return <>{closureReason}</>;
    }

    if (openingHour === 'CLOSED' || closingHour === 'CLOSED') {
        return <>Market currently closed</>;
    }

    const renderRange = (opening: string, closing: string) => (
        <span style={{ alignItems: 'center', display: 'inline-flex', gap: '0.2rem', whiteSpace: 'nowrap' }}>
            <span>{stripUtcOffset(opening)}</span>
            <TbArrowRight
                aria-hidden
                size='0.75rem'
                style={{ color: token.colorTextTertiary, display: 'block', flex: 'none' }}
            />
            <span>{stripUtcOffset(closing)}</span>
        </span>
    );

    return (
        <span
            style={{
                alignItems: 'flex-end',
                display: 'inline-flex',
                flexDirection: 'column',
                gap: '0.125rem',
            }}
        >
            {renderRange(openingHour, closingHour)}
            {openingAdditional && closingAdditional ? renderRange(openingAdditional, closingAdditional) : null}
        </span>
    );
};

const formatLocalTime = (now: Date, timeZone: string) =>
    new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        hour12: true,
        minute: '2-digit',
        timeZone,
    }).format(now);

const formatLocalDateTime = (now: Date, timeZone: string) =>
    new Intl.DateTimeFormat('en-US', {
        day: 'numeric',
        hour: 'numeric',
        hour12: true,
        minute: '2-digit',
        month: 'short',
        timeZone,
        weekday: 'short',
    }).format(now);

const formatTimezonePath = (timeZone: string) => {
    const region = formatTimezoneRegion(timeZone);
    const label = formatTimezoneLabel(timeZone);

    return region === label ? label : `${region} / ${label}`;
};

const MarketStatusPopover: FC<MarketStatusPopoverProps> = ({
    city,
    flagSrc,
    isError = false,
    isLoading = false,
    marketHours,
    now,
    popoverWidth = '25rem',
    timeZone,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const hubHours = useMemo(() => getFinancialMarketHubHours(timeZone, marketHours), [marketHours, timeZone]);
    const exchangeCodes = useMemo(() => hubHours.map(({ exchange }) => exchange), [hubHours]);
    const {
        data: marketHolidays = [],
        isError: marketHolidaysError,
        isPending: marketHolidaysPending,
    } = useQuery(marketHoursQueryOptions.holidays(exchangeCodes, isPopoverOpen));
    const holidaysByExchange = useMemo(
        () =>
            marketHolidays.reduce<Map<string, FmpExchangeMarketHoliday[]>>((result, holiday) => {
                const exchange = holiday.exchange.trim().toUpperCase();
                const exchangeHolidays = result.get(exchange) ?? [];

                exchangeHolidays.push(holiday);
                result.set(exchange, exchangeHolidays);

                return result;
            }, new Map()),
        [marketHolidays],
    );
    const primaryMarket = getPrimaryFinancialMarketHour(timeZone, marketHours);
    const primaryStatus = primaryMarket?.isMarketOpen ? 'Open' : primaryMarket ? 'Closed' : 'Status unavailable';
    const primaryBadgeStatus = primaryMarket?.isMarketOpen ? 'success' : 'default';
    const exchangeInformationLoading =
        (isLoading && hubHours.length === 0) ||
        (isPopoverOpen && exchangeCodes.length !== 0 && marketHolidaysPending && marketHolidays.length === 0);
    const exchangeInformationError =
        (isError && hubHours.length === 0) || (isPopoverOpen && marketHolidaysError && marketHolidays.length === 0);
    const content = (
        <CompactHoverCardSurface style={{ marginTop: '-0.125rem' }} width={popoverWidth}>
            <CompactHoverCardSection divider={false} style={{ padding: '0.5rem 0.675rem' }}>
                <Row align='middle' justify='space-between' wrap={false}>
                    <Col style={{ alignItems: 'center', display: 'inline-flex', gap: '0.5rem', minWidth: 0 }}>
                        {flagSrc ? (
                            <Avatar
                                alt={`${city} flag`}
                                shape='square'
                                src={flagSrc}
                                style={{
                                    border: `1px solid ${token.colorBorder}`,
                                    borderRadius: token.borderRadius,
                                    flex: 'none',
                                    height: '1.5rem',
                                    width: city === 'Geneva' ? '1.5rem' : '2rem',
                                }}
                            />
                        ) : null}
                        <div style={{ minWidth: 0 }}>
                            <strong>{city}</strong>
                            <small style={{ color: token.colorTextTertiary, display: 'block' }}>
                                {formatTimezonePath(timeZone)}
                            </small>
                        </div>
                    </Col>
                    <Col style={{ textAlign: 'right' }}>
                        <small>{formatLocalDateTime(now, timeZone)}</small>
                    </Col>
                </Row>
            </CompactHoverCardSection>
            <CompactHoverCardSection style={{ padding: '0.5rem 0.675rem' }}>
                {exchangeInformationLoading ? (
                    <ExchangeInformationSkeleton rowCount={Math.max(hubHours.length, 2)} />
                ) : exchangeInformationError ? (
                    <EmptyView
                        description='Live exchange information could not be loaded. Please try again shortly.'
                        height='8.5rem'
                        icon={<TbBuildingBank size='1.25rem' />}
                        label='Exchange Information Unavailable'
                    />
                ) : hubHours.length !== 0 ? (
                    hubHours.map((marketHour, index) => {
                        const exchangeHolidays = holidaysByExchange.get(marketHour.exchange.trim().toUpperCase()) ?? [];
                        const closureReason = getMarketClosureReason(marketHour, now, exchangeHolidays);
                        const nextTransition = getNextMarketSessionTransition(marketHour, now, exchangeHolidays);

                        return (
                            <Row
                                key={marketHour.exchange}
                                align='top'
                                style={{
                                    columnGap: '0.75rem',
                                    marginBottom: index === hubHours.length - 1 ? 0 : '0.5rem',
                                }}
                                wrap={false}
                            >
                                <Col flex='auto' style={{ minWidth: 0 }}>
                                    <span
                                        style={{
                                            alignItems: 'center',
                                            display: 'inline-flex',
                                            gap: '0.375rem',
                                            minWidth: 0,
                                        }}
                                    >
                                        <Badge status={marketHour.isMarketOpen ? 'success' : 'default'} />
                                        <small style={{ lineHeight: 1, whiteSpace: 'nowrap' }}>{marketHour.name}</small>
                                        {marketHour.exchange === primaryMarket?.exchange ? (
                                            <SmallTag compact message='Primary' styles={{ marginLeft: '0.375rem' }} />
                                        ) : null}
                                    </span>
                                    <small
                                        style={{
                                            alignItems: 'center',
                                            color: token.colorTextTertiary,
                                            display: 'flex',
                                            paddingLeft: '1rem',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        <span>{marketHour.exchange}</span>
                                        <MiddleDot horizontalMargin='0.25rem' styles={{ flex: 'none' }} />
                                        <span>{marketHour.isMarketOpen ? 'Open' : 'Closed'}</span>
                                        {nextTransition ? (
                                            <>
                                                <MiddleDot horizontalMargin='0.25rem' styles={{ flex: 'none' }} />
                                                <span>
                                                    {marketHour.isMarketOpen ? 'Closes' : 'Opens'} in{' '}
                                                    {formatDate(nextTransition, 'mini')}
                                                </span>
                                            </>
                                        ) : null}
                                    </small>
                                </Col>
                                <Col flex='none' style={{ maxWidth: '10.5rem', textAlign: 'right' }}>
                                    <small>{formatLocalTime(now, marketHour.timezone)}</small>
                                    <small
                                        style={{
                                            alignItems: 'center',
                                            color: token.colorTextTertiary,
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                        }}
                                    >
                                        <SessionTimeRange closureReason={closureReason} marketHour={marketHour} />
                                    </small>
                                </Col>
                            </Row>
                        );
                    })
                ) : (
                    <EmptyView
                        description='No live exchange information is available for this market.'
                        height='8.5rem'
                        icon={<TbBuildingBank size='1.25rem' />}
                        label='No Exchange Information'
                    />
                )}
            </CompactHoverCardSection>
        </CompactHoverCardSurface>
    );

    return (
        <Dropdown
            arrow={false}
            destroyPopupOnHide
            getPopupContainer={() => document.body}
            mouseEnterDelay={0.15}
            onOpenChange={setIsPopoverOpen}
            popupRender={() => content}
            overlayStyle={{ maxWidth: popoverWidth, minWidth: popoverWidth, zIndex: token.zIndexPopupBase + 1500 }}
            placement='bottomLeft'
            trigger={['hover']}
        >
            <span
                aria-label={`${city} primary market: ${isLoading ? 'loading' : primaryStatus}`}
                style={{
                    alignItems: 'center',
                    cursor: 'default',
                    display: 'inline-flex',
                    gap: '0.375rem',
                    minWidth: 0,
                }}
            >
                {flagSrc ? (
                    <Avatar
                        alt={`${city} flag`}
                        shape='square'
                        src={flagSrc}
                        style={{
                            border: `1px solid ${token.colorBorder}`,
                            borderRadius: token.borderRadius,
                            flex: 'none',
                            height: '1rem',
                            width: city === 'Geneva' ? '1rem' : '1.5rem',
                        }}
                    />
                ) : null}
                <small>{city}</small>
                <Badge status={primaryBadgeStatus} />
            </span>
        </Dropdown>
    );
};

export default MarketStatusPopover;
